'use client'

import { Button, Cell, Image, Spacer, Text } from '@vinted/web-ui'
import { notFound } from 'next/navigation'
import { useEffect } from 'react'

import { useFetch, HttpStatus } from '@marketplace-web/core-api/api-client-util'
import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import { useIsDarkMode } from '@marketplace-web/dark-mode/dark-mode-feature'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import { ErrorState } from '@marketplace-web/error-display/error-display-feature'
import {
  getKycEducationInfo,
  KycEducationSectionModel,
  transformKycEducationDtos,
} from '@marketplace-web/pay-identity/pay-identity-kyc-data'
import { useRefUrl } from '@marketplace-web/ref-url/ref-url-feature'

import { ROOT_URL } from '../../constants/routes'

type Props = {
  onClick?: () => void
  isBusiness?: boolean
}

const KycEducation = ({ onClick, isBusiness = false }: Props) => {
  const breakpoint = useBreakpoint()
  const isDarkMode = useIsDarkMode()
  const refUrl = useRefUrl(ROOT_URL)
  const translate = useTranslate('kyc_education')

  const {
    fetch: fetchEducationInfo,
    transformedData: kycEducationInfo,
    isLoading: isEducationLoading,
    error: educationLoadError,
  } = useFetch(getKycEducationInfo, response =>
    transformKycEducationDtos(response.education.sections),
  )

  useEffect(() => {
    fetchEducationInfo()
  }, [fetchEducationInfo])

  function renderActionButtons() {
    return (
      <div className="u-flexbox u-flex-wrap-reverse u-justify-content-flex-end">
        {!isBusiness && (
          <>
            <Button
              type="button"
              text={translate('actions.cancel')}
              inline={breakpoint.tabletsUp}
              url={refUrl}
            />
            <Spacer size="medium" orientation={breakpoint.phones ? 'horizontal' : 'vertical'} />
          </>
        )}
        {onClick && (
          <Button
            type="button"
            text={translate('actions.start')}
            styling="filled"
            inline={breakpoint.tabletsUp}
            onClick={onClick}
            testId="kyc-education-start-button"
          />
        )}
      </div>
    )
  }

  function renderSection(section: KycEducationSectionModel) {
    const iconUrl = isDarkMode ? section.darkIconUrl : section.iconUrl

    return (
      <Cell
        key={section.title}
        title={section.title}
        body={<Text as="span" text={section.body} html />}
        prefix={<Image src={iconUrl} size="medium" alt="" />}
        styling="wide"
      />
    )
  }

  const renderErrorScreen = () => {
    if (educationLoadError?.status === HttpStatus.NotFound) return notFound()

    return <ErrorState />
  }

  function renderContent() {
    if (educationLoadError) return renderErrorScreen()
    if (!kycEducationInfo?.length || isEducationLoading)
      return <ContentLoader testId="kyc-education-loader" />

    return (
      <>
        <Cell styling="wide">
          <Text as="h1" type="heading" text={translate('header.title')} />
        </Cell>
        {kycEducationInfo.map(renderSection)}
        <Spacer size="x-large" />
        {renderActionButtons()}
      </>
    )
  }

  return (
    <div className={isBusiness ? 'container' : 'medium-container'} data-testid="kyc-education">
      {renderContent()}
    </div>
  )
}

export default KycEducation
